import { EMPTY_PREBID_MODULE, MultipleEditPayload, PrebidModule, PrebidModuleDataBySite } from '@pubstack/common/src/prebidModule'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAnalyticsModule, useRealTimeDataModule } from '~/api/adm-api.hook'
import { usePrebidModulesCatalog } from '~/api/catalog.api'
import { useToast } from '~/components/Toast/useToasts'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PureAdmPrebidModulePage } from '~/modules/adm/inventory/modules/PureAdmPrebidModulePage'
import { configurationState } from '~/state'

export const AdmRealTimeDataModulePage = () => {
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)
  const [prebidModule, setPrebidModule] = useState<PrebidModule>(EMPTY_PREBID_MODULE)
  const [moduleDataBySite, setModuleDataBySite] = useState<PrebidModuleDataBySite<'RTD'>[]>([])
  const [enabledGreenbidsAnalytics, setEnabledGreenbidsAnalytics] = useState<PrebidModuleDataBySite<'Analytics'>[]>([])

  const { moduleId } = useParams()
  const breadcrumbs = useBreadcrumbs({ integrationName: prebidModule.displayName ?? '' })

  const { all, byId } = useRealTimeDataModule(null)
  const { all: allAnalytics } = useAnalyticsModule(null)
  const { byId: catalogById, isLoading: isCatalogLoading } = usePrebidModulesCatalog()

  const toast = useToast()
  const navigate = useNavigate()

  const updateSiteRTD = async (siteData: PrebidModuleDataBySite<'RTD'>) => {
    try {
      if (moduleId) {
        const update = { name: prebidModule.code, ...siteData.moduleData }
        await byId.put(moduleId, siteData.siteId, update, siteData.enabled)
        toast.admSuccess(siteData.label, true, navigate)
        setModuleDataBySite(moduleDataBySite.map((data) => (data.siteId === siteData.siteId ? { ...data, moduleData: update, enabled: siteData.enabled } : data)))
      }
    } catch (_e) {
      toast.alert('An error has occurred, please retry later.')
    }
  }

  const onMultipleEdit = async (payload: MultipleEditPayload<'RTD'>) => {
    try {
      if (moduleId) {
        await byId.multipleEdit(moduleId, payload)
        toast.admSuccess(`${payload.selectedSites.length} site${payload.selectedSites.length > 1 ? 's' : ''}`, true, navigate)
        loadData()
      }
    } catch (_e) {
      toast.alert('An error has occurred, please retry later.')
    }
  }

  const loadData = async () => {
    if (moduleId && catalogById[moduleId]) {
      const prebidModule = catalogById[moduleId]
      setPrebidModule(prebidModule)
      const res = await all.get(moduleId)
      const isGreenbidRtdModule = prebidModule.code === 'greenbidsRtdProvider'
      if (isGreenbidRtdModule) {
        const greenbidsAnalyticsSiteConfigs = await allAnalytics.get('3fffdae8-e591-41c0-9343-9d2e5651fd51')
        const anEnableGreenbidsAnalyitcsSiteConfig = greenbidsAnalyticsSiteConfigs.filter((greenbidsAnalyticsSiteConfig) => greenbidsAnalyticsSiteConfig.enabled)
        setEnabledGreenbidsAnalytics(anEnableGreenbidsAnalyitcsSiteConfig)
      }

      setModuleDataBySite(res)
    }
  }

  useEffect(() => {
    loadData()
  }, [moduleId, catalogById])

  // needed to Omit the name
  const data = useMemo(
    () =>
      moduleDataBySite.map(({ moduleData, ...data }) => ({
        ...data,
        moduleData: moduleData ? { params: moduleData?.params, storage: moduleData?.storage, waitForIt: moduleData?.waitForIt } : prebidModule.defaultValues,
      })),
    [moduleDataBySite, prebidModule]
  )

  return (
    <PureAdmPrebidModulePage
      prebidModule={prebidModule}
      breadcrumbs={breadcrumbs}
      data={data}
      helpText={'realTimeData>dataProviders'}
      isLoading={all.loading || byId.loading || isCatalogLoading}
      updateSiteData={updateSiteRTD}
      baseCDNUrl={STATIC_ASSETS_CDN}
      onBackClick={() => navigate(-1)}
      onMultipleEdit={onMultipleEdit}
      enabledGreenbidsAnalytics={enabledGreenbidsAnalytics}
    />
  )
}
